import type { TagClass } from "@/lib/status";

/**
 * Réservation de stock adossée à un virement bancaire.
 *
 * Le virement met plusieurs jours à arriver : le stock est donc bloqué avec une
 * échéance, et l'écran doit dire d'un coup d'œil combien de temps il reste.
 * Une date brute ne suffit pas — « 12/08 » n'alerte personne, « expire dans
 * 4 h » si.
 */

/**
 * Au-delà de deux prolongations, la réservation cesse d'être une exception :
 * soit le client ne paiera pas, soit un accord commercial devrait être formalisé
 * autrement que par des reports successifs.
 */
export const PROLONGATIONS_AVANT_ALERTE = 2;

export interface EtatReservation {
	label: string;
	tone: TagClass;
	expiree: boolean;
}

/** Durée lisible : au-delà de 48 h on compte en jours, personne ne lit « 73 h ». */
function dureeLisible(ms: number): string {
	const minutes = Math.round(ms / 60_000);
	if (minutes < 60) return `${minutes} min`;
	const heures = Math.round(minutes / 60);
	if (heures < 48) return `${heures} h`;
	return `${Math.round(heures / 24)} j`;
}

export function etatReservation(
	expiresAt: string | null | undefined,
	maintenant: number = Date.now(),
): EtatReservation {
	if (!expiresAt) {
		return { label: "Aucune échéance", tone: "neutral", expiree: false };
	}
	const restant = new Date(expiresAt).getTime() - maintenant;
	if (Number.isNaN(restant)) {
		return { label: "Échéance illisible", tone: "neutral", expiree: false };
	}
	if (restant <= 0) {
		return {
			label: `Échue depuis ${dureeLisible(-restant)}`,
			tone: "danger",
			expiree: true,
		};
	}
	return {
		label: `Expire dans ${dureeLisible(restant)}`,
		// Sous 24 h, la libération automatique peut tomber avant le prochain
		// passage de l'opérateur : c'est le moment de relancer ou de prolonger.
		tone: restant <= 24 * 3_600_000 ? "warning" : "info",
		expiree: false,
	};
}

export function estTropProlongee(extensions: number | null | undefined): boolean {
	return (extensions ?? 0) > PROLONGATIONS_AVANT_ALERTE;
}
